/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ComponentProps, ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { SpriteIcon } from '@/components/ui/sprite-icon';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

type SearchButtonProps = Omit<ComponentProps<typeof Button>, 'variant' | 'aria-label' | 'children'>;

export default function SearchButton({ className, ...props }: SearchButtonProps): ReactElement {
    const { t } = useTranslation('header');

    return (
        <Button
            variant="ghost"
            aria-label={t('searchPlaceholder')}
            className={cn(
                'cursor-pointer !p-1 m-1 hover:bg-header-menu-icon-hover-background group-data-[transparent=true]/header:hover:bg-header-transparent-menu-icon-hover-background! transition-opacity h-auto',
                className
            )}
            {...props}>
            <SpriteIcon
                name="search"
                className="size-8 group-data-[transparent=true]/header:text-white! group-data-[transparent=true]/header:fill-white!"
            />
        </Button>
    );
}
